import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@services/adminConsoleService';
import type { ConfigValueItem, ConfigValueDetail } from '@services/adminConsoleService';

interface SubmitTimeConfig {
  Id: number;
  ConfigValueID: number;
  ItemType: string;
  LayerId: number;
  LayerName: string;
  CustomFieldId: number;
  CustomField: string;
  Required: boolean;
  Editable: boolean;
}

const EMPTY_FORM = {
  configValueId: null as number | null,
  itemType: '',
  layerId: null as number | null,
  layerName: '',
  customFieldId: null as number | null,
  customField: '',
  required: false,
  editable: false,
};

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

interface ConfigModalProps {
  title: string;
  form: typeof EMPTY_FORM;
  itemTypes: ConfigValueItem[];
  detail: ConfigValueDetail | null;
  detailLoading: boolean;
  saving: boolean;
  error: string | null;
  onItemTypeChange: (id: number, name: string) => void;
  onLayerChange: (id: number, name: string) => void;
  onFormChange: (patch: Partial<typeof EMPTY_FORM>) => void;
  onClearError: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

const ConfigModal: React.FC<ConfigModalProps> = ({
  title,
  form,
  itemTypes,
  detail,
  detailLoading,
  saving,
  error,
  onItemTypeChange,
  onLayerChange,
  onFormChange,
  onClearError,
  onConfirm,
  onClose,
}) =>
{
  const customFields = useMemo(() =>
  {
    if (!detail || form.layerId === null) return [];
    return detail.Layers.find(l => l.LayerId === form.layerId)?.CustomFields ?? [];
  }, [detail, form.layerId]);

  const hasLayer = !!detail && !detailLoading;
  const hasCF = hasLayer && form.layerId !== null;

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[500px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">{title}</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-4">
          {error && (
            <div className="px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] dark:text-[#8d312c]">
              {error}
            </div>
          )}

          {/* Item Type */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Item Type:</label>
            <select
              value={form.configValueId ?? ''}
              onChange={e =>
              {
                const id = Number(e.target.value);
                const item = itemTypes.find(i => i.ConfigValueID === id);
                onClearError();
                onItemTypeChange(id, item?.Value ?? '');
              }}
              className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb] bg-white"
            >
              <option value="">Select...</option>
              {itemTypes.map(i => (
                <option key={i.ConfigValueID} value={i.ConfigValueID}>
                  {i.Value}
                </option>
              ))}
            </select>
          </div>

          {/* Layer */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Layer:</label>
            {detailLoading ? (
              <div className="flex items-center gap-2 text-[12px] text-[#aaa]">
                <span className="material-icons animate-spin text-[14px] text-[#62aacb]">
                  refresh
                </span>
                Loading layers…
              </div>
            ) : (
              <select
                value={form.layerId ?? ''}
                onChange={e =>
                {
                  const id = Number(e.target.value);
                  const layer = detail?.Layers.find(l => l.LayerId === id);
                  onClearError();
                  onLayerChange(id, layer?.Name ?? '');
                }}
                disabled={!hasLayer}
                className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb] bg-white disabled:bg-[#f5f5f5] disabled:text-[#aaa]"
              >
                <option value="">Select...</option>
                {detail?.Layers.map(l => (
                  <option key={l.LayerId} value={l.LayerId}>
                    {l.Name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Custom Detail */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Custom Detail:</label>
            <select
              value={form.customFieldId ?? ''}
              onChange={e =>
              {
                const id = Number(e.target.value);
                const cf = customFields.find(f => f.CustomFieldId === id);
                onClearError();
                onFormChange({ customFieldId: id, customField: cf?.Name ?? '' });
              }}
              disabled={!hasCF}
              className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb] bg-white disabled:bg-[#f5f5f5] disabled:text-[#aaa]"
            >
              <option value="">Select...</option>
              {customFields.map(cf => (
                <option key={cf.CustomFieldId} value={cf.CustomFieldId}>
                  {cf.Name}
                </option>
              ))}
            </select>
          </div>

          {/* Checkboxes */}
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <span className="corgee_com_checkbox">
                <input
                  id="stc-required"
                  type="checkbox"
                  checked={form.required}
                  onChange={e =>
                  {
                    if (e.target.checked)
                    {
                      onFormChange({ required: true, editable: true });
                    }
                    else
                    {
                      onFormChange({ required: false });
                    }
                  }}
                />
                <label htmlFor="stc-required"></label>
              </span>
              <label
                htmlFor="stc-required"
                className="text-[12px] text-[#555] cursor-pointer select-none"
              >
                Required
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span className="corgee_com_checkbox">
                <input
                  id="stc-editable"
                  type="checkbox"
                  checked={form.editable}
                  onChange={e =>
                    onFormChange(
                      e.target.checked ? { editable: true } : { editable: false, required: false },
                    )
                  }
                />
                <label htmlFor="stc-editable"></label>
              </span>
              <label
                htmlFor="stc-editable"
                className="text-[12px] text-[#555] cursor-pointer select-none"
              >
                Editable
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] transition-colors disabled:opacity-50 dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] transition-colors disabled:opacity-50 dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
          >
            {saving ? '…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────

interface DeleteModalProps {
  cfg: SubmitTimeConfig;
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ cfg, saving, error, onConfirm, onClose }) =>
  createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[400px] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">Delete Configuration</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        <div className="px-5 py-4 text-[12px] text-[#555] flex flex-col gap-2">
          {error && (
            <div className="px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[#c62828]">
              {error}
            </div>
          )}
          <p>Are you sure you want to delete this configuration?</p>
          <div className="bg-[#f5f5f5] border border-[#e0e0e0] rounded px-3 py-2 text-[11px] text-[#666] flex flex-col gap-1">
            <span>
              <span className="text-[#A5A7AC]">Item Type:</span> {cfg.ItemType}
            </span>
            <span>
              <span className="text-[#A5A7AC]">Layer:</span> {cfg.LayerName}
            </span>
            <span>
              <span className="text-[#A5A7AC]">Custom Detail:</span> {cfg.CustomField}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] transition-colors disabled:opacity-50"
          >
            {saving ? '…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const SubmitTimeConfigTab: React.FC = () =>
{
  const [configs, setConfigs] = useState<SubmitTimeConfig[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [itemTypes, setItemTypes] = useState<ConfigValueItem[]>([]);
  const [detail, setDetail] = useState<ConfigValueDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modal state
  const [modal, setModal] = useState<'add' | 'edit' | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<SubmitTimeConfig | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadConfigs = useCallback(async () =>
  {
    try
    {
      const data = await adminConsoleService.getSubmitTimeConfigurations();
      setConfigs(data);
    }
    catch
    {
      // non-fatal
    }
    finally
    {
      setListLoading(false);
    }
  }, []);

  useEffect(() =>
  {
    loadConfigs();
    adminConsoleService
      .getConfigValueAll()
      .then(items => setItemTypes(items))
      .catch(() => {});
  }, [loadConfigs]);

  // Filtered list
  const filtered = useMemo(() =>
  {
    const q = search.trim().toLowerCase();
    if (!q) return configs;
    return configs.filter(
      c =>
        c.ItemType.toLowerCase().includes(q) ||
        c.LayerName.toLowerCase().includes(q) ||
        c.CustomField.toLowerCase().includes(q),
    );
  }, [configs, search]);

  // Load detail when Item Type changes inside modal
  const handleItemTypeChange = async (id: number, itemType: string) =>
  {
    setForm({ ...EMPTY_FORM, configValueId: id, itemType });
    setDetail(null);
    if (!id) return;
    setDetailLoading(true);
    try
    {
      const d = await adminConsoleService.getConfigValueById(id);
      setDetail(d);
    }
    catch
    {
      // ignore
    }
    finally
    {
      setDetailLoading(false);
    }
  };

  const handleLayerChange = (layerId: number, layerName: string) =>
  {
    setForm(prev => ({ ...prev, layerId, layerName, customFieldId: null, customField: '' }));
  };

  // Open Add modal
  const openAdd = () =>
  {
    setForm({ ...EMPTY_FORM });
    setDetail(null);
    setModalError(null);
    setEditingId(null);
    setModal('add');
  };

  // Open Edit modal
  const openEdit = async (cfg: SubmitTimeConfig) =>
  {
    setEditingId(cfg.Id);
    setModalError(null);
    setDetail(null);
    setForm({
      configValueId: cfg.ConfigValueID,
      itemType: cfg.ItemType,
      layerId: cfg.LayerId,
      layerName: cfg.LayerName,
      customFieldId: cfg.CustomFieldId,
      customField: cfg.CustomField,
      required: cfg.Required,
      editable: cfg.Editable,
    });
    setModal('edit');
    setDetailLoading(true);
    try
    {
      const d = await adminConsoleService.getConfigValueById(cfg.ConfigValueID);
      setDetail(d);
    }
    catch
    {
      // ignore
    }
    finally
    {
      setDetailLoading(false);
    }
  };

  const closeModal = () =>
  {
    setModal(null);
    setForm({ ...EMPTY_FORM });
    setDetail(null);
    setEditingId(null);
    setModalError(null);
  };

  // Save
  const handleSave = async () =>
  {
    if (!form.configValueId || form.layerId === null || !form.customFieldId)
    {
      setModalError('Please fill in all required fields.');
      return;
    }

    const duplicate = configs.find(
      c =>
        c.ConfigValueID === form.configValueId &&
        c.LayerId === form.layerId &&
        c.CustomFieldId === form.customFieldId &&
        c.Id !== editingId,
    );
    if (duplicate)
    {
      setModalError(
        `The combination of Item Type "${form.itemType}", Layer "${form.layerName}" and Custom Detail "${form.customField}" already exists.`,
      );
      return;
    }

    setSaving(true);
    setModalError(null);
    try
    {
      if (editingId !== null)
      {
        await adminConsoleService.deleteSubmitTimeConfiguration(editingId);
      }
      await adminConsoleService.addSubmitTimeConfiguration({
        ConfigValueID: form.configValueId,
        ItemType: form.itemType,
        LayerId: form.layerId,
        LayerName: form.layerName,
        CustomFieldId: form.customFieldId,
        CustomField: form.customField,
        Required: form.required,
        Editable: form.editable,
      });
      closeModal();
      setListLoading(true);
      await loadConfigs();
    }
    catch
    {
      setModalError('Failed to save configuration. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () =>
  {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    setDeleteError(null);
    try
    {
      await adminConsoleService.deleteSubmitTimeConfiguration(deleteTarget.Id);
      setConfigs(prev => prev.filter(c => c.Id !== deleteTarget.Id));
      setDeleteTarget(null);
    }
    catch
    {
      setDeleteError('Failed to delete configuration. Please try again.');
    }
    finally
    {
      setDeleteSaving(false);
    }
  };

  return (
    <div className="px-6 py-5">
      {/* ── Header row ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        {/* Left: search */}
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#555] whitespace-nowrap">Find by keyword:</span>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search configurations..."
              className="border border-[#d0d0d0] rounded px-3 py-1 text-[12px] outline-none focus:border-[#62aacb] w-[200px]"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#555]"
              >
                <span className="material-icons text-[13px]">close</span>
              </button>
            )}
          </div>
        </div>

        {/* Right: Add button */}
        <button
          onClick={openAdd}
          className="flex items-center gap-1 text-[12px] text-[#5697b3] hover:text-[#3d7fa8] transition-colors"
        >
          <span className="material-icons text-[18px] dark:text-[#beb7ac] ">
            add_circle_outline
          </span>
          <span className="hover:underline">Add Submit Time Configuration</span>
        </button>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="border border-[#ddd] rounded-sm bg-white shadow-sm overflow-hidden">
        {listLoading ? (
          <div className="flex items-center gap-2 p-5 text-[12px] text-[#aaa]">
            <span className="material-icons animate-spin text-[16px] text-[#62aacb]">refresh</span>
            Loading…
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                <th className="text-left px-4 py-2 text-[#555] font-medium">Item Type</th>
                <th className="text-left px-4 py-2 text-[#555] font-medium">Layer</th>
                <th className="text-left px-4 py-2 text-[#555] font-medium">Custom Detail</th>
                <th className="text-center px-4 py-2 text-[#555] font-medium w-[80px]">Required</th>
                <th className="text-center px-4 py-2 text-[#555] font-medium w-[80px]">Editable</th>
                <th className="text-right px-4 py-2 text-[#555] font-medium w-[120px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-6 text-[#aaa]">
                    {search ? 'No results match your search.' : 'No configurations found.'}
                  </td>
                </tr>
              ) : (
                filtered.map((cfg, i) => (
                  <tr
                    key={cfg.Id}
                    className={`border-b border-[#f0f0f0] ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f0f6fb] transition-colors`}
                  >
                    <td className="px-4 py-2 text-[#5697b3]">{cfg.ItemType}</td>
                    <td className="px-4 py-2 text-[#5697b3]">{cfg.LayerName}</td>
                    <td className="px-4 py-2 text-[#555]">{cfg.CustomField}</td>
                    <td className="px-4 py-2 text-center text-[#555]">
                      {cfg.Required ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-2 text-center text-[#555]">
                      {cfg.Editable ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openEdit(cfg)}
                          className="px-3 py-1 bg-[#62aacb] text-white rounded text-[11px] hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                          {
                            setDeleteError(null);
                            setDeleteTarget(cfg);
                          }}
                          className="px-3 py-1 bg-[#e57373] text-white rounded text-[11px] hover:bg-[#d32f2f] dark:bg-[#8d312c] dark:hover:brightness-[0.85] transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <ConfigModal
          title={
            modal === 'add' ? 'Add Submit Time Configuration' : 'Edit Submit Time Configuration'
          }
          form={form}
          itemTypes={itemTypes}
          detail={detail}
          detailLoading={detailLoading}
          saving={saving}
          error={modalError}
          onItemTypeChange={handleItemTypeChange}
          onLayerChange={handleLayerChange}
          onFormChange={patch => setForm(prev => ({ ...prev, ...patch }))}
          onClearError={() => setModalError(null)}
          onConfirm={handleSave}
          onClose={closeModal}
        />
      )}

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          cfg={deleteTarget}
          saving={deleteSaving}
          error={deleteError}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
